import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Award, Target, TrendingUp, Users, Cloud, Gauge, Workflow, Database, Cpu } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const Home = () => {
  const { isDark } = useTheme();

  const services = [
    {
      icon: <Workflow className="w-10 h-10" />,
      title: 'Power Platform Solutions',
      description: 'Low-code automation with Power Apps, Power Automate, and Copilot Studio for rapid digital transformation.',
      colorAccent: 'primary',
    },
    {
      icon: <Database className="w-10 h-10" />,
      title: 'Enterprise Integrations',
      description: 'Seamless Xero, NetSuite, and Azure integration solutions that unify your business systems.',
      colorAccent: 'teal',
    },
    {
      icon: <Cpu className="w-10 h-10" />,
      title: 'Custom Development',
      description: 'Full-stack applications built with modern cloud-native architecture and best practices.',
      colorAccent: 'sky',
    },
  ];

  const benefits = [
    { icon: <Award className="w-10 h-10" />, title: 'Integration Experts', description: 'Certified specialists in Power Platform, Xero, NetSuite, and Azure ecosystems.' },
    { icon: <Target className="w-10 h-10" />, title: 'Tailored Solutions', description: 'Custom strategies designed for your unique business challenges and goals.' },
    { icon: <TrendingUp className="w-10 h-10" />, title: 'Proven Results', description: 'Track record of successful implementations across diverse industries.' },
    { icon: <Users className="w-10 h-10" />, title: 'End-to-End Support', description: 'Comprehensive service from planning through deployment and beyond.' },
    { icon: <Cloud className="w-10 h-10" />, title: 'Cloud-First Approach', description: 'Modern, scalable solutions leveraging Microsoft Azure infrastructure.' },
    { icon: <Gauge className="w-10 h-10" />, title: 'Rapid Deployment', description: 'Agile methodology ensuring quick time-to-value for your investment.' },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className={`relative min-h-screen flex items-center justify-center pt-20 ${isDark ? 'bg-dark-bg' : 'bg-light-bg'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                className={`inline-block px-5 py-2 rounded-full mb-6 text-sm font-semibold ${
                  isDark ? 'shadow-neu-dark-sm' : 'shadow-neu-light-sm'
                }`}
                style={{ background: isDark ? '#212529' : '#E8EDF2' }}
              >
                <span className="text-brand-primary">✨ Integration Specialists</span>
              </motion.div>

              <h1 className={`text-5xl lg:text-7xl font-bold font-manrope mb-6 leading-tight ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                Seamless
                <br />
                <span className="text-brand-primary">Integration,</span>
                <br />
                Infinite Possibilities
              </h1>

              <p className={`text-lg lg:text-xl mb-10 leading-relaxed ${isDark ? 'text-dark-text-secondary' : 'text-light-text-secondary'}`}>
                Transform your business with Power Platform, Xero, NetSuite, and custom enterprise solutions. We deliver end-to-end integration services that connect your systems and unlock operational efficiency.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className={`inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-base font-semibold transition-all duration-300 transform hover:-translate-y-1 active:translate-y-0 bg-brand-primary hover:bg-brand-primary-hover text-white ${
                    isDark ? 'shadow-neu-dark hover:shadow-neu-dark-hover' : 'shadow-neu-light hover:shadow-neu-light-hover'
                  }`}
                  data-testid="hero-cta-consultation"
                >
                  Schedule Consultation
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/services"
                  className={`inline-flex items-center justify-center px-8 py-4 rounded-full text-base font-semibold transition-all duration-300 transform hover:-translate-y-1 active:translate-y-0 ${
                    isDark 
                      ? 'text-dark-text shadow-neu-dark hover:shadow-neu-dark-hover' 
                      : 'text-light-text shadow-neu-light hover:shadow-neu-light-hover'
                  }`}
                  style={{ background: isDark ? '#212529' : '#E8EDF2' }}
                  data-testid="hero-cta-services"
                >
                  Explore Services
                </Link>
              </div>
            </motion.div>

            {/* Right Visual */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className={`relative ${isDark ? 'shadow-neu-dark' : 'shadow-neu-light'} rounded-3xl overflow-hidden`}>
                <img
                  src="https://images.unsplash.com/photo-1764263996452-d8498c9b0ce8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwzfHx0ZWNobm9sb2d5JTIwbWluaW1hbGlzdCUyMGFic3RyYWN0fGVufDB8fHx8MTc3MTg1Njg1MXww&ixlib=rb-4.1.0&q=85"
                  alt="Technology Integration"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-primary/20 to-transparent" />
              </div>
              
              {/* Floating badges */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
                className={`absolute top-8 -left-4 px-4 py-2 rounded-xl font-semibold text-sm backdrop-blur-sm ${
                  isDark ? 'bg-dark-bg/80 text-dark-text shadow-neu-dark-sm' : 'bg-light-bg/80 text-light-text shadow-neu-light-sm'
                }`}
              >
                Power Apps
              </motion.div>
              <motion.div
                animate={{ y: [0, -15, 0] }}
                transition={{ duration: 4, repeat: Infinity, delay: 0.5 }}
                className={`absolute bottom-8 -right-4 px-4 py-2 rounded-xl font-semibold text-sm backdrop-blur-sm ${
                  isDark ? 'bg-dark-bg/80 text-dark-text shadow-neu-dark-sm' : 'bg-light-bg/80 text-light-text shadow-neu-light-sm'
                }`}
              >
                Xero & NetSuite
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className={`py-24 ${isDark ? 'bg-dark-bg' : 'bg-light-bg'}`} data-testid="services-overview">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className={`text-4xl lg:text-5xl font-bold font-manrope mb-4 ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
              Our Core <span className="text-brand-primary">Services</span>
            </h2>
            <p className={`text-lg max-w-3xl mx-auto ${isDark ? 'text-dark-text-secondary' : 'text-light-text-secondary'}`}>
              Comprehensive integration and development services for modern businesses.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                className={`p-8 rounded-2xl transition-all duration-300 group ${
                  isDark ? 'shadow-neu-dark hover:shadow-neu-dark-hover' : 'shadow-neu-light hover:shadow-neu-light-hover'
                }`}
                style={{ background: isDark ? '#212529' : '#E8EDF2' }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -8 }}
              >
                <div className={`inline-flex p-4 rounded-2xl mb-6 ${isDark ? 'shadow-neu-dark-sm' : 'shadow-neu-light-sm'}`} style={{ background: isDark ? '#212529' : '#E8EDF2' }}>
                  <span className="text-brand-primary">{service.icon}</span>
                </div>
                <h3 className={`text-2xl font-bold font-manrope mb-3 ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                  {service.title}
                </h3>
                <p className={`leading-relaxed ${isDark ? 'text-dark-text-secondary' : 'text-light-text-secondary'}`}>
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.div
            className="text-center mt-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-brand-primary hover:text-brand-primary-hover font-semibold transition-colors"
              data-testid="view-all-services-link"
            >
              View All Services
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className={`py-24 ${isDark ? 'bg-dark-bg' : 'bg-light-bg'}`} data-testid="why-choose-us">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className={`text-4xl lg:text-5xl font-bold font-manrope mb-4 ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
              Why <span className="text-brand-primary">SerendiTech</span>
            </h2>
            <p className={`text-lg max-w-3xl mx-auto ${isDark ? 'text-dark-text-secondary' : 'text-light-text-secondary'}`}>
              Trusted expertise delivering measurable results for businesses worldwide.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                className={`p-8 rounded-2xl transition-all duration-300 text-center ${
                  isDark ? 'shadow-neu-dark hover:shadow-neu-dark-hover' : 'shadow-neu-light hover:shadow-neu-light-hover'
                }`}
                style={{ background: isDark ? '#212529' : '#E8EDF2' }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className={`inline-flex p-4 rounded-2xl mb-6 ${isDark ? 'shadow-neu-dark-pressed' : 'shadow-neu-light-pressed'}`} style={{ background: isDark ? '#212529' : '#E8EDF2' }}>
                  <span className="text-brand-primary">{benefit.icon}</span>
                </div>
                <h3 className={`text-xl font-bold font-manrope mb-3 ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                  {benefit.title}
                </h3>
                <p className={`text-sm leading-relaxed ${isDark ? 'text-dark-text-secondary' : 'text-light-text-secondary'}`}>
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={`py-24 ${isDark ? 'bg-dark-bg' : 'bg-light-bg'}`} data-testid="cta-section">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <motion.div
            className={`p-12 lg:p-16 rounded-3xl text-center ${
              isDark ? 'shadow-neu-dark' : 'shadow-neu-light'
            }`}
            style={{ background: isDark ? '#212529' : '#E8EDF2' }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className={`text-4xl lg:text-5xl font-bold font-manrope mb-6 ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
              Ready to <span className="text-brand-primary">Transform</span> Your Business?
            </h2>
            <p className={`text-lg mb-10 max-w-2xl mx-auto ${isDark ? 'text-dark-text-secondary' : 'text-light-text-secondary'}`}>
              Let's discuss how our integration expertise can streamline your operations and accelerate growth.
            </p>
            <Link
              to="/contact"
              className={`inline-flex items-center gap-2 px-8 py-4 rounded-full text-base font-semibold transition-all duration-300 transform hover:-translate-y-1 active:translate-y-0 bg-brand-primary hover:bg-brand-primary-hover text-white ${
                isDark ? 'shadow-neu-dark hover:shadow-neu-dark-hover' : 'shadow-neu-light hover:shadow-neu-light-hover'
              }`}
              data-testid="cta-button-bottom"
            >
              Get Started Today
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
